import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { Plus, GripVertical, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { adminApi } from "../../api/admin.api.js";
import { call } from "../../api/client.js";
import { useResource } from "../../hooks/useResource.js";
import { Button } from "../../components/ui/Button.jsx";
import { Modal } from "../../components/ui/Modal.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Spinner } from "../../components/ui/Spinner.jsx";
import { ImageUpload, ImageThumbnail } from "../../components/upload/ImageUpload.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { cn } from "../../lib/cn.js";

/**
 * Gallery page — admin manages every photo that appears on the public
 * /gallery route. Each photo is grouped by SECTION (Interior or
 * Exterior); optionally tagged with a Design ref for attribution.
 *
 * One section filter + one search box. Bulk upload lives on a separate
 * page (room for previews + progress feedback).
 */
const SECTIONS = [
  { value: "interior", label: "Interior" },
  { value: "exterior", label: "Exterior" },
];

export default function GalleryPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [orderDirty, setOrderDirty] = useState(false);
  const [localOrder, setLocalOrder] = useState([]);

  useEffect(() => {
    document.title = "Gallery · Admin";
  }, []);

  const { remove } = useResource("gallery", { toastLabel: "gallery image" });

  // Designs are optional but we still surface them in the form so admins
  // can attribute a photo to a specific home design if they want.
  const { data: designs } = useQuery({
    queryKey: ["designs", "all", "for-gallery"],
    queryFn: () => adminApi.list("designs", { limit: 200, sort: "sortOrder:asc" }),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["gallery", "all", { section: sectionFilter }],
    queryFn: () =>
      adminApi.list("gallery", {
        limit: 500,
        sort: "sortOrder:asc",
        ...(sectionFilter ? { section: sectionFilter } : {}),
      }),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data?.items) {
      setLocalOrder(data.items.map((g) => g._id));
      setOrderDirty(false);
    }
  }, [data]);

  const reorderMutation = useMutation({
    mutationFn: (ids) => call("patch", "/admin/gallery/reorder", { data: { ids } }),
    onSuccess: () => {
      toast.success("Order saved");
      setOrderDirty(false);
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message ?? "Failed to save order"),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const byId = new Map((data?.items ?? []).map((g) => [g._id, g]));
  const filtered = localOrder
    .map((id) => byId.get(id))
    .filter(Boolean)
    .filter((g) =>
      search ? (g.title ?? "").toLowerCase().includes(search.toLowerCase()) : true
    );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localOrder.indexOf(active.id);
    const newIndex = localOrder.indexOf(over.id);
    setLocalOrder(arrayMove(localOrder, oldIndex, newIndex));
    setOrderDirty(true);
  };

  const designOptions = designs?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Catalogue</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Gallery</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.items?.length ?? "—"} photo
            {data?.items?.length === 1 ? "" : "s"}. Every photo lives under one of
            two sections — <b>Interior</b> or <b>Exterior</b>. Drag to reorder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {orderDirty && (
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              loading={reorderMutation.isPending}
              onClick={() => reorderMutation.mutate(localOrder)}
            >
              Save order
            </Button>
          )}
          <Link to="/gallery/bulk-import">
            <Button variant="secondary" leftIcon={<Upload className="h-4 w-4" />}>
              Bulk upload
            </Button>
          </Link>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({})}>
            New image
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search photos by title…"
        >
          <Select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="min-w-[160px]"
            aria-label="Filter by section"
          >
            <option value="">All sections</option>
            {SECTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </FilterBar>

        {isLoading ? (
          <div className="py-16 text-center">
            <Spinner size="md" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-text-muted">
            {search || sectionFilter
              ? "No matches."
              : 'No photos yet — click "New image" or "Bulk upload" to add some.'}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-border-subtle bg-bg-elevated">
                {filtered.map((g, i) => (
                  <GalleryRow
                    key={g._id}
                    image={g}
                    index={i}
                    onEdit={() => setEditing(g)}
                    onDelete={() => setConfirmDelete(g)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {editing && (
        <GalleryFormModal
          row={editing._id ? editing : null}
          designs={designOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["gallery"] });
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title}"?`}
        description="The photo will be removed from the public gallery."
        confirmLabel="Delete photo"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}

function GalleryRow({ image, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image._id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const sectionLabel =
    SECTIONS.find((s) => s.value === image.section)?.label ?? image.section;
  const designName = image.design?.name;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-4 px-4 py-3 transition-colors hover:bg-bg-elevated-2",
        isDragging && "opacity-50"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab text-text-muted hover:text-text-primary active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-8 text-xs font-mono text-text-muted">#{index + 1}</span>
      <ImageThumbnail src={image.image} alt={image.title} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">{image.title}</p>
        <p className="truncate text-xs text-text-muted">
          {sectionLabel}
          {designName ? ` · ${designName}` : ""}
        </p>
      </div>
      {image.isFeatured && <Badge tone="accent">featured</Badge>}
      <RowActions onEdit={onEdit} onDelete={onDelete} />
    </li>
  );
}

function GalleryFormModal({ row, designs, onClose, onSaved }) {
  const queryClient = useQueryClient();
  const isNew = !row;
  const form = useForm({
    defaultValues: {
      title: "",
      image: "",
      section: "interior",
      design: "",
      isFeatured: false,
      sortOrder: 0,
      ...(row
        ? {
            ...row,
            section: row.section ?? "interior",
            // List response populates design as {_id, name, slug}; the
            // form needs the id string (or empty for none).
            design: row.design?._id ?? row.design ?? "",
          }
        : {}),
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = form;

  const errToast = (label) => (err) => {
    const details = err?.response?.data?.details;
    if (Array.isArray(details) && details.length > 0) {
      toast.error(`${details[0].path}: ${details[0].message}`);
    } else {
      toast.error(err?.response?.data?.message ?? label);
    }
  };

  const create = useMutation({
    mutationFn: (data) => adminApi.create("gallery", data),
    onSuccess: () => {
      toast.success("Gallery image added");
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      onSaved();
    },
    onError: errToast("Create failed"),
  });
  const update = useMutation({
    mutationFn: (data) => adminApi.update("gallery", row._id, data),
    onSuccess: () => {
      toast.success("Gallery image updated");
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      onSaved();
    },
    onError: errToast("Update failed"),
  });

  const submit = handleSubmit((values) => {
    const payload = { ...values };
    if (payload.sortOrder === "") payload.sortOrder = 0;
    // Empty string from the design dropdown means "no design" — store null.
    payload.design = payload.design || null;
    if (isNew) create.mutate(payload);
    else update.mutate(payload);
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? "Add gallery image" : `Edit "${row?.title}"`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting}>
            {isNew ? "Add image" : "Save changes"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Title / caption"
          required
          error={errors.title?.message}
          placeholder="e.g. Marble island kitchen"
          {...register("title", { required: "Title is required" })}
        />

        <Controller
          control={control}
          name="image"
          rules={{ required: "Upload a photo" }}
          render={({ field, fieldState }) => (
            <div>
              <ImageUpload
                label="Photo"
                required
                subdir="gallery"
                aspect="video"
                value={field.value}
                onChange={field.onChange}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-xs text-danger">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Section"
            required
            error={errors.section?.message}
            {...register("section", { required: "Pick a section" })}
          >
            {SECTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>

          <Select
            label="Linked home design (optional)"
            hint="Use this only if the photo is of a specific design."
            {...register("design")}
          >
            <option value="">— None —</option>
            {designs.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input type="number" label="Sort order" {...register("sortOrder")} />
          <Controller
            control={control}
            name="isFeatured"
            render={({ field }) => (
              <Switch
                label="Show on homepage"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}
